import { useCallback, useMemo, useState } from "react";
import reportsService from "../../services/surveyApi/reports.service";
import { IDateRange } from "./Reports";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { FormatDate } from "../../utils/conversion";
import moment from "moment";
import { useTranslation } from "react-i18next";

// Define the type for each item in the select options
interface ISelectOption {
  value: number;
  label: string;
}

export default function ReportsLogic() {
  const [dataReportList, setDataReportList] = useState([]);
  const [dataReportListFiltered, setDataReportListFiltered] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportmessage, setReportMessage] = useState<string>("");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [t] = useTranslation("global");

  const monthsLabel: string[] = useMemo(() => {
    return [
      t("header.january"),
      t("header.february"),
      t("header.march"),
      t("header.april"),
      t("header.may"),
      t("header.june"),
      t("header.july"),
      t("header.august"),
      t("header.september"),
      t("header.october"),
      t("header.november"),
      t("header.december"),
    ];
  }, [t]);

  const getReport = (
    { value, sp }: { value: string; sp: string },
    date: IDateRange
  ) => {
    if (sp === "GetWeeklyCapturesReport") {
      getWeeklyCapturesReport({ value, sp }, date);
      return;
    }

    if (sp === "GetMonthlyCapturesReport") {
      getMonthlyCapturesReport({ value, sp }, date);
      return;
    }

    let newStartDate = FormatDate(date?.startDate, "DD/MM/YYYY");
    let newEndDate = FormatDate(date?.endDate, "DD/MM/YYYY");

    if (sp === "GetUsersReport") {
      newStartDate = "01/01/1990"; //Estas fechas no se usan pero las dejare por si en un futuro las necesitan. Se envian por ahora con una fecha cualquiera para que no falle la peticion
      newEndDate = "01/01/1990";
    }

    setLoading(true);
    const nameExcel = value.trim().replace(/\s+/g, "_");
    reportsService
      .getReportEndPoint(sp, newStartDate, newEndDate, languageID?.value)
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            setReportMessage("donwload_failed");
          } else if (r instanceof Blob) {
            try {
              const url = window.URL.createObjectURL(r);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", nameExcel + ".xlsx");
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
              setReportMessage("successfully_generated");
            } catch (error) {
              console.error("Error al descargar el archivo:", error);
            }
          }
        } catch (error) {
          console.error("Error al descargar el archivo:", error);
          setReportMessage("donwload_failed");
        } finally {
          setLoading(false);
        }
      });
  };

  const getReportList = () => {
    reportsService.getReportListEndPoint(languageID?.value).then((r) => {
      try {
        if (r && r?.statusCode !== 401) {
          const data = r.map((item: any) => {
            return {
              value: item.fieldID,
              label: item.fieldValue,
              sp: item.fieldSP,
            };
          });
          setDataReportList(data);
          setDataReportListFiltered(data);
        }
      } catch (error) {
        console.error("Error al descargar el archivo:", error);
      }
    });
  };

  const handleChangeSearchReport = (value: string) => {
    const filterArray = dataReportList.filter((element: any) => {
      if (
        element.value
          .toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()) ||
        element.label
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase())
      ) {
        return element;
      }
    });
    setDataReportListFiltered(filterArray);
  };

  const getWeeklyCapturesReport = (
    { value, sp }: { value: string; sp: string },
    date: IDateRange
  ) => {
    const newWeekDate = FormatDate(date?.startDate, "MM/DD/YYYY");
    setLoading(true);
    const nameExcel = value.trim().replace(/\s+/g, "_");
    reportsService
      .getReportWeeklyEndPoint(sp, newWeekDate, languageID?.value)
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            setReportMessage("donwload_failed");
          } else if (r instanceof Blob) {
            try {
              const url = window.URL.createObjectURL(r);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", nameExcel + ".xlsx");
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
              setReportMessage("successfully_generated");
            } catch (error) {
              console.error("Error al descargar el archivo:", error);
            }
          }
        } catch (error) {
          console.error("Error al descargar el archivo:", error);
          setReportMessage("donwload_failed");
        } finally {
          setLoading(false);
        }
      });
  };

  const getMonthlyCapturesReport = (
    { value, sp }: { value: string; sp: string },
    date: IDateRange
  ) => {
    const monthDate = FormatDate(date?.startDate, "MM/DD/YYYY");
    setLoading(true);
    const nameExcel = value.trim().replace(/\s+/g, "_");
    reportsService
      .getReportMonthlyEndPoint(sp, monthDate, languageID?.value)
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            setReportMessage("donwload_failed");
          } else if (r instanceof Blob) {
            try {
              const url = window.URL.createObjectURL(r);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", nameExcel + ".xlsx");
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
              setReportMessage("successfully_generated");
            } catch (error) {
              console.error("Error al descargar el archivo:", error);
            }
          }
        } catch (error) {
          console.error("Error al descargar el archivo:", error);
          setReportMessage("donwload_failed");
        } finally {
          setLoading(false);
        }
      });
  };

  const calculateYearsAvailablesToSelect = useCallback((): ISelectOption[] => {
    const yearStart = 2024;
    const currentYear = moment().year();
    const years: ISelectOption[] = [];
    for (let index = yearStart; index <= currentYear; index++) {
      years.push({
        value: index,
        label: index.toString(),
      });
    }

    return years;
  }, []);

  const calculateMonthsAvailablesToSelect = useCallback(
    (year: number | undefined): ISelectOption[] => {
      if (!year || year === 0) return [];

      const months: ISelectOption[] = [];
      let maxMonth: number = 11;

      if (year === moment().year()) {
        maxMonth = moment().month() - 1; // set as max month the before month
      }

      for (let index = 0; index <= maxMonth; index++) {
        months.push({
          value: index,
          label: monthsLabel[index],
        });
      }

      return months;
    },
    [monthsLabel]
  );

  return {
    getReportList,
    getReport,
    dataReportList,
    dataReportListFiltered,
    loading,
    reportmessage,
    handleChangeSearchReport,
    calculateMonthsAvailablesToSelect,
    calculateYearsAvailablesToSelect,
  };
}
